import type { SajuAnalysis } from '../types/input';
import type { EarthlyBranch } from '../types/branches';
import type { StarCross } from '../types/compatibility';
import { branchIndex } from '../core/sexagenary';
import { getSamhapGroup, TWELVE_SINSAL_TABLE } from '../sinsal/definitions';
import { EARTHLY_BRANCHES } from '../core/constants';

const DOHWA_INDEX = 4;   // 연살(도화) in TWELVE_SINSAL_TABLE
const YEOKMA_INDEX = 9;  // 역마 in TWELVE_SINSAL_TABLE

/**
 * 두 사주의 도화(연살)·역마 교차를 분석한다.
 *
 * 각 사람의 년지/일지 기준 도화·역마 지지를 산출하고,
 * 상대 사주에 해당 지지가 존재하는지 확인한다.
 */
export function analyzeStarCross(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
  noHour: boolean = false,
): StarCross {
  const fp1 = analysis1.fourPillars;
  const fp2 = analysis2.fourPillars;

  // 각 사람의 도화/역마 지지 (년지, 일지 기준)
  const p1DoHwa = getStarBranches(fp1.year.branch, fp1.day.branch, DOHWA_INDEX);
  const p2DoHwa = getStarBranches(fp2.year.branch, fp2.day.branch, DOHWA_INDEX);
  const p1YeokMa = getStarBranches(fp1.year.branch, fp1.day.branch, YEOKMA_INDEX);
  const p2YeokMa = getStarBranches(fp2.year.branch, fp2.day.branch, YEOKMA_INDEX);

  // 상대 사주의 모든 지지 (noHour 시 시주 placeholder 제외)
  const branches1 = getAllBranches(analysis1, noHour);
  const branches2 = getAllBranches(analysis2, noHour);

  // 교차 확인: 내 도화/역마 지지가 상대 사주에 있는지
  const doHwaCross =
    p1DoHwa.some(b => branches2.includes(b)) ||
    p2DoHwa.some(b => branches1.includes(b));

  const yeokMaCross =
    p1YeokMa.some(b => branches2.includes(b)) ||
    p2YeokMa.some(b => branches1.includes(b));

  const parts: string[] = [];
  if (doHwaCross) parts.push('도화 교차 (매력적 끌림)');
  if (yeokMaCross) parts.push('역마 교차 (활동적 관계)');
  const description = parts.length > 0
    ? `도화역마: ${parts.join(', ')}`
    : '도화역마: 교차 없음';

  return {
    person1DoHwa: p1DoHwa,
    person2DoHwa: p2DoHwa,
    person1YeokMa: p1YeokMa,
    person2YeokMa: p2YeokMa,
    doHwaCross,
    yeokMaCross,
    description,
  };
}

/** 년지/일지 기준으로 특정 12신살의 지지를 가져온다 (중복 제거) */
function getStarBranches(yearBranch: EarthlyBranch, dayBranch: EarthlyBranch, sinsalIndex: number): EarthlyBranch[] {
  const yearGroup = getSamhapGroup(branchIndex(yearBranch));
  const dayGroup = getSamhapGroup(branchIndex(dayBranch));

  const yearTarget = TWELVE_SINSAL_TABLE[yearGroup][sinsalIndex];
  const dayTarget = TWELVE_SINSAL_TABLE[dayGroup][sinsalIndex];

  const result: EarthlyBranch[] = [EARTHLY_BRANCHES[yearTarget]];
  if (dayTarget !== yearTarget) {
    result.push(EARTHLY_BRANCHES[dayTarget]);
  }
  return result;
}

function getAllBranches(analysis: SajuAnalysis, noHour: boolean): EarthlyBranch[] {
  const fp = analysis.fourPillars;
  const branches = [fp.year.branch, fp.month.branch, fp.day.branch];
  if (!noHour) branches.push(fp.hour.branch);
  return branches;
}
